<template>
  <div class="container-fluid">
    <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-4">
      <h1 class="h3 mb-2 mb-md-0 text-gray-800">About Page - Manage Artists</h1>
      <router-link to="/admin/artist/add" class="btn btn-primary align-self-end align-self-md-center">
        <i class="fas fa-plus"></i> Add Artist
      </router-link>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Artists List -->
    <div v-else class="card shadow mb-4">
      <div class="card-header py-3">
        <h6 class="m-0 font-weight-bold text-primary">Artists</h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-bordered mb-0 border-0" width="100%" cellspacing="0">
            <thead>
              <tr>
                <th>Order</th>
                <th>Name</th>
                <th class="d-none d-md-table-cell">Subtitle</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="artist in artists" :key="artist._id" :class="{ 'table-secondary': !artist.enabled }">
                <td style="width: 50px;">{{ artist.sortOrder }}</td>
                <td>{{ artist.name }}</td>
                <td class="d-none d-md-table-cell">{{ artist.subtitle || '-' }}</td>
                <td style="width: 100px;">
                  <span class="status-indicator d-md-none" :class="artist.enabled ? 'bg-success' : 'bg-secondary'" :title="artist.enabled ? 'Enabled' : 'Disabled'"></span>
                  <span class="badge d-none d-md-inline-block" :class="artist.enabled ? 'badge-success' : 'badge-secondary'">
                    {{ artist.enabled ? 'Enabled' : 'Disabled' }}
                  </span>
                </td>
                <td style="width: 200px;">
                  <router-link :to="`/admin/artist/edit/${artist._id}`" class="btn btn-sm btn-primary mr-1" title="Edit">
                    <i class="fas fa-edit"></i>
                  </router-link>
                  <button 
                    class="btn btn-sm mr-1" 
                    :class="artist.enabled ? 'btn-warning' : 'btn-success'"
                    @click="toggleArtist(artist)" 
                    :title="artist.enabled ? 'Disable' : 'Enable'"
                  >
                    <i class="fas" :class="artist.enabled ? 'fa-eye-slash' : 'fa-eye'"></i>
                  </button>
                  <button class="btn btn-sm btn-danger" @click="confirmDelete(artist)" title="Delete">
                    <i class="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="artists.length === 0">
                <td colspan="5" class="text-center">No artists found. Click "Add Artist" to create one.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal fade" id="deleteModal" tabindex="-1" role="dialog" aria-labelledby="deleteModalLabel" aria-hidden="true">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="deleteModalLabel">Confirm Delete</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click="closeDeleteModal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            Are you sure you want to delete <strong>{{ selectedArtist?.name }}</strong>? 
            This will hide the artist from the website.
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDeleteModal">Cancel</button>
            <button type="button" class="btn btn-danger" @click="deleteArtist" :disabled="deleting">
              <span v-if="deleting" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
              {{ deleting ? 'Deleting...' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast notifications -->
    <div class="position-fixed bottom-0 right-0 p-3" style="z-index: 9999; right: 0; bottom: 0;">
      <div v-if="toast.show" class="toast show" role="alert" aria-live="assertive" aria-atomic="true">
        <div class="toast-header" :class="toast.type === 'success' ? 'bg-success text-white' : 'bg-danger text-white'">
          <strong class="mr-auto">{{ toast.type === 'success' ? 'Success' : 'Error' }}</strong>
          <button type="button" class="ml-2 mb-1 close text-white" @click="toast.show = false">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="toast-body">
          {{ toast.message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import fetchWrapper from '@/utils/fetchWrapper';

export default {
  name: 'AdminPagesAbout',
  data() {
    return {
      artists: [],
      loading: true,
      deleting: false,
      selectedArtist: null,
      toast: {
        show: false,
        type: 'success',
        message: ''
      }
    };
  },
  created() {
    this.fetchArtists();
  },
  methods: {
    async fetchArtists() {
      this.loading = true;
      try {
        const response = await fetchWrapper('/api/artists');
        const data = await response.json();
        
        if (data.status === 'success') {
          this.artists = data.artists;
        } else {
          this.showToast('error', data.message || 'Failed to fetch artists');
        }
      } catch (error) {
        console.error('Error fetching artists:', error);
        this.showToast('error', 'Failed to fetch artists');
      } finally {
        this.loading = false;
      }
    },
    
    async toggleArtist(artist) {
      try {
        const response = await fetchWrapper(`/api/artists/${artist._id}/toggle`, {
          method: 'PATCH'
        });
        
        const data = await response.json();
        
        if (data.status === 'success') {
          this.showToast('success', `Artist ${data.artist.enabled ? 'enabled' : 'disabled'} successfully`);
          await this.fetchArtists();
        } else {
          this.showToast('error', data.message || 'Failed to toggle artist status');
        }
      } catch (error) {
        console.error('Error toggling artist:', error);
        this.showToast('error', 'Failed to toggle artist status');
      }
    },
    
    confirmDelete(artist) {
      this.selectedArtist = artist;
      // eslint-disable-next-line no-undef
      $('#deleteModal').modal('show');
    },
    
    closeDeleteModal() {
      // eslint-disable-next-line no-undef
      $('#deleteModal').modal('hide');
    },
    
    async deleteArtist() {
      this.deleting = true;
      try {
        const response = await fetchWrapper(`/api/artists/${this.selectedArtist._id}`, {
          method: 'DELETE'
        });
        
        const data = await response.json();
        
        if (data.status === 'success') {
          this.showToast('success', 'Artist deleted successfully');
          this.closeDeleteModal();
          await this.fetchArtists();
        } else {
          this.showToast('error', data.message || 'Failed to delete artist');
        }
      } catch (error) {
        console.error('Error deleting artist:', error);
        this.showToast('error', 'Failed to delete artist');
      } finally {
        this.deleting = false;
      }
    },
    
    showToast(type, message) {
      this.toast = { show: true, type, message };
      setTimeout(() => {
        this.toast.show = false;
      }, 5000);
    }
  }
}
</script>

<style scoped>
.toast {
  min-width: 250px;
}

.status-indicator {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.table.border-0 {
  border: none !important;
}

.table.border-0 > thead > tr > th:first-child,
.table.border-0 > tbody > tr > td:first-child {
  border-left: none;
}

.table.border-0 > thead > tr > th:last-child,
.table.border-0 > tbody > tr > td:last-child {
  border-right: none;
}

.table.border-0 > tbody > tr:last-child > td {
  border-bottom: none;
}
</style>
